import React, { useMemo } from 'react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { invoicesApi, quotesApi, contactsApi, itemsApi, expensesApi } from '@/core/adapter-api';
import { FileText, Users, ShoppingCart, TrendingUp, Settings, Upload, Package, ArrowUpRight, ArrowDownRight, Euro, Clock, CheckCircle, AlertTriangle, Sparkles, Flower2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const fmt = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const StatCard = ({ label, value, icon: Icon, gradient, sub, trend, onClick }: any) => (
    <div onClick={onClick} className={`glow-card p-5 flex items-center gap-4 ${onClick ? 'cursor-pointer' : ''} animate-bloom`}>
        <div className={`p-3 rounded-2xl ${gradient} flex-shrink-0 shadow-lg`}>
            <Icon size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-pink-400 font-semibold uppercase tracking-wide">{label}</p>
            <h3 className="text-xl font-bold text-gray-700 truncate">{value}</h3>
            {sub && <p className={`text-xs mt-0.5 ${trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-rose-500' : 'text-pink-300'}`}>{sub}</p>}
        </div>
    </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white/90 backdrop-blur-lg border border-pink-100 rounded-2xl shadow-xl shadow-pink-100/30 p-3 text-sm">
                <p className="font-bold text-gray-700 mb-1">{label}</p>
                {payload.map((p: any) => (
                    <p key={p.name} style={{ color: p.color }}>{p.name === 'ingresos' ? '💰 Ingresos: ' : '📉 Gastos: '}{fmt(p.value)}</p>
                ))}
            </div>
        );
    }
    return null;
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate();

    const invoiceCount = useQuery(() => invoicesApi.count(), [], ['invoices']) ?? 0;
    const quoteCount = useQuery(() => quotesApi.count(), [], ['quotes']) ?? 0;
    const contactCount = useQuery(() => contactsApi.count(), [], ['contacts']) ?? 0;
    const itemCount = useQuery(() => itemsApi.count(), [], ['items']) ?? 0;

    const allInvoices = useQuery(() => invoicesApi.all(), [], ['invoices']);
    const allExpenses = useQuery(() => expensesApi.all(), [], ['expenses']);
    const allQuotes = useQuery(() => quotesApi.all(), [], ['quotes']);

    const stats = useMemo(() => {
        if (!allInvoices || !allExpenses || !allQuotes) return null;
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

        const isMonth = (ts: any, m: number, y: number) => {
            const d = new Date(ts);
            return d.getMonth() === m && d.getFullYear() === y;
        };

        const paidInvoices = allInvoices.filter(i => i.status === 'paid');
        const pendingInvoices = allInvoices.filter(i => ['sent', 'overdue'].includes(i.status));
        const overdueInvoices = allInvoices.filter(i => i.status === 'overdue');

        const thisMonthRevenue = paidInvoices
            .filter(i => isMonth(i.date || i.createdAt || 0, thisMonth, thisYear))
            .reduce((a, i) => a + (i.grandTotal || 0), 0);
        const lastMonthRevenue = paidInvoices
            .filter(i => isMonth(i.date || i.createdAt || 0, lastMonth, lastMonthYear))
            .reduce((a, i) => a + (i.grandTotal || 0), 0);

        const pendingAmount = pendingInvoices.reduce((a, i) => a + (i.grandTotal || 0), 0);
        const thisMonthExpenses = allExpenses
            .filter(e => isMonth(e.date || 0, thisMonth, thisYear))
            .reduce((a, e) => a + (e.total || 0), 0);

        const convertedQuotes = allQuotes.filter(q => ['accepted', 'invoiced'].includes(q.status)).length;
        const convRate = allQuotes.length > 0 ? Math.round((convertedQuotes / allQuotes.length) * 100) : 0;

        const chartData = Array.from({ length: 6 }, (_, i) => {
            const monthIdx = (thisMonth - 5 + i + 12) % 12;
            const yearIdx = thisMonth - 5 + i < 0 ? thisYear - 1 : thisYear;
            const monthName = new Date(yearIdx, monthIdx, 1).toLocaleDateString('es-ES', { month: 'short' });
            const ingresos = paidInvoices
                .filter(inv => isMonth(inv.date || inv.createdAt || 0, monthIdx, yearIdx))
                .reduce((a, inv) => a + (inv.grandTotal || 0), 0);
            const gastos = allExpenses
                .filter(exp => isMonth(exp.date || 0, monthIdx, yearIdx))
                .reduce((a, exp) => a + (exp.total || 0), 0);
            return { mes: monthName.charAt(0).toUpperCase() + monthName.slice(1), ingresos, gastos };
        });

        const revTrend = lastMonthRevenue === 0 ? null : ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(0);

        return { thisMonthRevenue, pendingAmount, overdueInvoices, pendingInvoices, thisMonthExpenses, convRate, chartData, revTrend };
    }, [allInvoices, allExpenses, allQuotes]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
            {/* Decorative gradient blob */}
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-pink-200/30 via-rose-100/20 to-fuchsia-200/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-40 -left-16 w-48 h-48 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
                <div className="flex items-center gap-3">
                    <Flower2 size={28} className="text-pink-400" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-700">Panel de Control</h1>
                        <p className="text-pink-400 text-sm flex items-center gap-1.5">
                            <Sparkles size={12} />
                            Resumen general de tu actividad
                        </p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 relative">
                <StatCard label="Facturas" value={invoiceCount} icon={FileText} gradient="stat-rose" onClick={() => navigate('/invoices')} />
                <StatCard label="Presupuestos" value={quoteCount} icon={TrendingUp} gradient="stat-lavender"
                    sub={stats ? `${stats.convRate}% conversión` : ''} onClick={() => navigate('/quotes')} />
                <StatCard label="Contactos" value={contactCount} icon={Users} gradient="stat-coral" onClick={() => navigate('/contacts')} />
                <StatCard label="Catálogo" value={itemCount} icon={Package} gradient="stat-mint" onClick={() => navigate('/items')} />
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glow-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle size={16} className="text-emerald-400" />
                        <span className="text-xs font-bold text-pink-400 uppercase tracking-wide">Ingresos cobrados (mes)</span>
                    </div>
                    <p className="text-xl font-bold text-gray-700">{stats ? fmt(stats.thisMonthRevenue) : '—'}</p>
                    {stats?.revTrend && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${Number(stats.revTrend) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {Number(stats.revTrend) >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                            {Math.abs(Number(stats.revTrend))}% vs mes anterior
                        </p>
                    )}
                </div>
                <div className="glow-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <Clock size={16} className="text-amber-400" />
                        <span className="text-xs font-bold text-pink-400 uppercase tracking-wide">Pendiente de cobro</span>
                    </div>
                    <p className="text-xl font-bold text-gray-700">{stats ? fmt(stats.pendingAmount) : '—'}</p>
                    {stats && <p className="text-xs text-pink-300 mt-1">{stats.pendingInvoices.length} facturas</p>}
                </div>
                <div className="glow-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={16} className="text-rose-400" />
                        <span className="text-xs font-bold text-pink-400 uppercase tracking-wide">Vencidas</span>
                    </div>
                    <p className="text-xl font-bold text-rose-500">{stats ? stats.overdueInvoices.length : '—'}</p>
                    {stats && stats.overdueInvoices.length > 0 && (
                        <p className="text-xs text-rose-400 mt-1">{fmt(stats.overdueInvoices.reduce((a, i) => a + (i.grandTotal || 0), 0))}</p>
                    )}
                </div>
                <div className="glow-card p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <Euro size={16} className="text-fuchsia-400" />
                        <span className="text-xs font-bold text-pink-400 uppercase tracking-wide">Gastos (mes)</span>
                    </div>
                    <p className="text-xl font-bold text-gray-700">{stats ? fmt(stats.thisMonthExpenses) : '—'}</p>
                    {stats && <p className="text-xs text-pink-300 mt-1">vs {fmt(stats.thisMonthRevenue)} ingresos</p>}
                </div>
            </div>

            {/* Chart + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="glow-card lg:col-span-2 p-6">
                    <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <span>📊</span> Ingresos vs Gastos — últimos 6 meses
                    </h2>
                    {stats && stats.chartData.some(d => d.ingresos > 0 || d.gastos > 0) ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={stats.chartData} barGap={4} barSize={22}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#FCE7F3" vertical={false} />
                                <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#F472B6' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: '#F9A8D4' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#FDF2F8' }} />
                                <Bar dataKey="ingresos" fill="#EC4899" radius={[8, 8, 0, 0]} />
                                <Bar dataKey="gastos" fill="#C084FC" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-[220px] flex items-center justify-center text-pink-300 text-sm flex-col gap-3">
                            <TrendingUp size={36} className="text-pink-200" />
                            <p>Los datos aparecerán cuando haya facturas y gastos registrados.</p>
                        </div>
                    )}
                    <div className="flex gap-4 mt-3">
                        <span className="flex items-center gap-1.5 text-xs text-pink-400"><span className="w-3 h-3 rounded-full stat-rose inline-block" />Ingresos cobrados</span>
                        <span className="flex items-center gap-1.5 text-xs text-pink-400"><span className="w-3 h-3 rounded-full stat-lavender inline-block" />Gastos</span>
                    </div>
                </div>

                <div className="glow-card p-6">
                    <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <span>✨</span> Accesos Rápidos
                    </h2>
                    <div className="space-y-1.5">
                        {[
                            { path: '/invoices', icon: FileText, label: 'Nueva Factura', color: 'text-pink-500 hover:bg-pink-50' },
                            { path: '/quotes', icon: ShoppingCart, label: 'Nuevo Presupuesto', color: 'text-fuchsia-500 hover:bg-fuchsia-50' },
                            { path: '/contacts', icon: Users, label: 'Clientes', color: 'text-rose-500 hover:bg-rose-50' },
                            { path: '/expenses', icon: TrendingUp, label: 'Registrar Gasto', color: 'text-purple-500 hover:bg-purple-50' },
                            { path: '/settings', icon: Settings, label: 'Configuración', color: 'text-pink-400 hover:bg-pink-50' },
                            { path: '/import', icon: Upload, label: 'Importar Datos', color: 'text-pink-400 hover:bg-pink-50' },
                        ].map(({ path, icon: Icon, label, color }) => (
                            <button key={path} onClick={() => navigate(path)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${color}`}>
                                <Icon size={16} /> {label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Recent overdue invoices */}
            {stats && stats.overdueInvoices.length > 0 && (
                <div className="glow-card p-6">
                    <h2 className="text-base font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <AlertTriangle size={18} className="text-rose-400" /> Facturas vencidas
                    </h2>
                    <div className="space-y-2">
                        {stats.overdueInvoices.slice(0, 5).map((inv: any) => (
                            <div key={inv.id} className="flex items-center justify-between py-2 border-b border-pink-50 last:border-0">
                                <div>
                                    <p className="text-sm font-medium text-gray-700">{inv.number || inv.id.slice(0, 8)}</p>
                                    <p className="text-xs text-pink-300">{inv.contactName || '—'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold text-rose-500">{fmt(inv.grandTotal || 0)}</p>
                                    <button onClick={() => navigate('/invoices')} className="text-xs text-pink-500 hover:underline">Ver</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
